import React, { useEffect, useState } from 'react';
import { sbomApi, itemUrl } from './sbomApi';

/**
 * Where Used — every release that brings one component in, and the chain of packages that
 * pulls it there. A modal on top of whichever screen asked, so the user never loses their place.
 * By default it covers EVERY version of the package (each release says which version it ships);
 * "This version" narrows it to the one clicked.
 */
export default function SbomWhereUsedModal({ itemId, info, onClose, onAuth }) {
  const s = info.settings;
  const [wu, setWu] = useState(null);
  const [all, setAll] = useState(true);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let off = false;
    setWu(null);
    sbomApi.whereUsed(itemId, all)
      .then((d) => { if (!off) setWu(d); })
      .catch((e) => { if (!off && !onAuth(e)) setWu({ error: e.message }); });
    return () => { off = true; };
  }, [itemId, all, onAuth]);

  const link = (iid) => itemUrl(info.baseUrl, s.versionId, s.componentType, iid);
  const relLink = (iid) => itemUrl(info.baseUrl, s.versionId, s.releaseType, iid);
  const c = wu?.component;
  const n = wu?.releases?.length || 0;
  const versions = wu?.versions || [];

  return (
    <div className="sbom-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="sbom-modal" style={{ width: 'min(680px, 100%)' }} role="dialog" aria-modal="true" aria-labelledby="sbom-wu-title">
        <div className="sbom-modal-head">
          <div style={{ minWidth: 0 }}>
            <h3 id="sbom-wu-title">
              Where is {c ? <>{c.name}{!all && <> <span className="sbom-mono">{c.version}</span></>}</> : 'this component'} used?
            </h3>
            {c?.key && <a className="sbom-key" href={link(c.item_id)} target="_blank" rel="noreferrer">{c.key}</a>}
            {c?.purl && <div className="sbom-tiny sbom-muted sbom-mono" style={{ overflowWrap: 'anywhere' }}>{c.purl}</div>}
          </div>
          <button type="button" className="sbom-modal-x" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="sbom-modal-body">
          {!wu && <div className="sbom-muted">Loading…</div>}
          {wu?.error && <div className="sbom-error" role="alert">{wu.error}</div>}
          {c && (
            <div className="sbom-wu-bar">
              <div className="sbom-seg" role="group" aria-label="Which versions">
                <button type="button" aria-pressed={all} className={all ? 'on' : ''} onClick={() => setAll(true)}>All versions</button>
                <button type="button" aria-pressed={!all} className={!all ? 'on' : ''} onClick={() => setAll(false)}>
                  This version <span className="sbom-mono">{c.version}</span></button>
              </div>
              {all && versions.length > 0 && (
                <div className="sbom-wu-vers">
                  {versions.map((v) => (
                    <span key={v.item_id} className={`sbom-pill ${v.item_id === c.item_id ? 'sbom-pill--acc' : 'sbom-pill--plain'}`}
                      title={v.key}>
                      <span className="sbom-mono">{v.version || '—'}</span> · {v.releases} release{v.releases === 1 ? '' : 's'}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {c && (
            <div className="sbom-note">
              {n === 0 ? `No release includes this ${all ? 'package' : 'version'}.` : <>{all && versions.length > 1 ? <><b>{versions.length} versions</b>, used</> : 'Used'} in <b>{n} release{n === 1 ? '' : 's'}</b>. Each line shows how the package gets into that release.</>}
            </div>
          )}
          {c && wu.releases.map((x) => (
            <div key={x.release.item_id} className="sbom-wu-rel">
              <div className="sbom-wu-rel-h">
                <span className="sbom-glyph sbom-glyph--rel" aria-hidden="true">R</span>
                <b>{x.release.name || `Release ${x.release.item_id}`}</b>
                {x.release.key && <a className="sbom-key" href={relLink(x.release.item_id)} target="_blank" rel="noreferrer">{x.release.key}</a>}
                <span style={{ flex: 1 }} />
                {(x.versions || []).map((v) => (
                  <a key={v.item_id} className={`sbom-pill ${v.item_id === c.item_id ? 'sbom-pill--acc' : 'sbom-pill--plain'} sbom-rel-pill`}
                    href={link(v.item_id) || undefined} target="_blank" rel="noreferrer" title={v.key}>
                    <span className="sbom-mono">{v.version || '—'}</span></a>
                ))}
              </div>
              {x.paths.map((p, i) => (
                <div key={i} className="sbom-path">
                  <span className="sbom-muted">Release</span>
                  {p.map((node, j) => (
                    <React.Fragment key={j}>
                      <span className="sbom-muted"> › </span>
                      {j === p.length - 1 ? <><b>{node.name}</b> <span className="sbom-mono sbom-muted">{node.version}</span></> : node.name}
                    </React.Fragment>
                  ))}
                  <span className={`sbom-pill ${p.length === 1 ? 'sbom-pill--info' : 'sbom-pill--plain'}`} style={{ marginLeft: 8 }}>
                    {p.length === 1 ? 'Direct dependency' : `Brought in by ${p[p.length - 2].name}`}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="sbom-modal-foot">
          <button type="button" className="sbom-btn" onClick={onClose}>Close</button>
        </div>
      </section>
    </div>
  );
}
